import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { Link } from "react-router-dom";

import {
  isToday,
  isThisWeek,
  isThisMonth,
  isThisYear,
} from "date-fns";

import { auth } from "./firebaseConfig";
import "./userPaymentByBrand.css";

const API_URL = "https://api.malidag.com";

function UserPaymentByBrand() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [sortBy, setSortBy] =
    useState("revenue");

  /*
  |--------------------------------------------------------------------------
  | Load protected Stripe orders
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let cancelled = false;

    const loadOrders = async () => {
      try {
        setLoading(true);
        setError("");

        const user = auth.currentUser;

        if (!user) {
          throw new Error(
            "Admin authentication is required."
          );
        }

        const token =
          await user.getIdToken();

        const response = await fetch(
          `${API_URL}/api/admin/stripe/orders`,
          {
            method: "GET",
            headers: {
              Authorization:
                `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error(
              "Your admin session has expired."
            );
          }

          if (response.status === 403) {
            throw new Error(
              "This account does not have admin access."
            );
          }

          throw new Error(
            `Failed to load brand performance (${response.status}).`
          );
        }

        const data =
          await response.json();

        if (!cancelled) {
          setOrders(
            Array.isArray(data?.orders)
              ? data.orders
              : []
          );
        }
      } catch (err) {
        console.error(
          "Failed to load brand performance:",
          err
        );

        if (!cancelled) {
          setError(
            err?.message ||
              "Failed to load brand performance."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOrders();

    return () => {
      cancelled = true;
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Helpers
  |--------------------------------------------------------------------------
  */

  const formatMoney = (
    amount,
    currency = "USD"
  ) => {
    const value = Number(amount);

    if (!Number.isFinite(value)) {
      return "—";
    }

    try {
      return new Intl.NumberFormat(
        "en-US",
        {
          style: "currency",
          currency,
        }
      ).format(value);
    } catch {
      return `${value.toFixed(
        2
      )} ${currency}`;
    }
  };

  const formatPercent = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "—";
    }

    return `${number.toFixed(2)}%`;
  };

  const getOrderDate = (order) => {
    const rawDate =
      order?.paidAt ||
      order?.createdAt ||
      order?.updatedAt;

    if (!rawDate) {
      return null;
    }

    const date = new Date(rawDate);

    return Number.isNaN(
      date.getTime()
    )
      ? null
      : date;
  };

  const createPeriod = () => ({
    revenue: 0,
    cost: 0,
    profit: 0,
    units: 0,
    orderIds: new Set(),
    incompleteCost: false,
  });

  const addToPeriod = (
    period,
    {
      revenue,
      cost,
      quantity,
      orderId,
      hasCost,
    }
  ) => {
    period.revenue += revenue;
    period.units += quantity;

    if (orderId) {
      period.orderIds.add(orderId);
    }

    if (hasCost) {
      period.cost += cost;
      period.profit +=
        revenue - cost;
    } else {
      period.incompleteCost = true;
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Brand aggregation
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  | A Stripe order can contain products from multiple brands.
  | Therefore we aggregate order.items[] rather than the order itself.
  |
  */

  const brandData = useMemo(() => {
    const map = {};

    orders.forEach((order) => {
      const date =
        getOrderDate(order);

      if (!date) {
        return;
      }

      const orderItems =
        Array.isArray(order?.items)
          ? order.items
          : [];

      const orderId =
        order?.stripePaymentIntentId ||
        order?._id ||
        "";

      orderItems.forEach((item) => {
        const brandName =
          String(
            item?.brandName ||
              "Unknown"
          ).trim() || "Unknown";

        const quantityValue =
          Number(item?.quantity);

        const quantity =
          Number.isFinite(
            quantityValue
          ) &&
          quantityValue > 0
            ? quantityValue
            : 1;

        /*
         * Prefer the frozen line total.
         * Fall back to unit price × quantity.
         */

        const storedLineTotal =
          Number(
            item?.usdLineTotal
          );

        const unitPrice =
          Number(
            item?.usdUnitPrice
          );

        let revenue = 0;

        if (
          Number.isFinite(
            storedLineTotal
          )
        ) {
          revenue =
            storedLineTotal;
        } else if (
          Number.isFinite(unitPrice)
        ) {
          revenue =
            unitPrice * quantity;
        }

        const brandPrice =
          Number(
            item?.brandPrice
          );

        const hasCost =
          Number.isFinite(
            brandPrice
          );

        const cost =
          hasCost
            ? brandPrice *
              quantity
            : 0;

        if (!map[brandName]) {
          map[brandName] = {
            brandName,

            revenue: 0,
            cost: 0,
            profit: 0,

            units: 0,
            orderIds:
              new Set(),

            incompleteCost:
              false,

            today:
              createPeriod(),

            week:
              createPeriod(),

            month:
              createPeriod(),

            year:
              createPeriod(),
          };
        }

        const brand =
          map[brandName];

        brand.revenue +=
          revenue;

        brand.units +=
          quantity;

        if (orderId) {
          brand.orderIds.add(
            orderId
          );
        }

        if (hasCost) {
          brand.cost += cost;
          brand.profit +=
            revenue - cost;
        } else {
          brand.incompleteCost =
            true;
        }

        const payload = {
          revenue,
          cost,
          quantity,
          orderId,
          hasCost,
        };

        if (isToday(date)) {
          addToPeriod(
            brand.today,
            payload
          );
        }

        if (
          isThisWeek(date, {
            weekStartsOn: 1,
          })
        ) {
          addToPeriod(
            brand.week,
            payload
          );
        }

        if (
          isThisMonth(date)
        ) {
          addToPeriod(
            brand.month,
            payload
          );
        }

        if (
          isThisYear(date)
        ) {
          addToPeriod(
            brand.year,
            payload
          );
        }
      });
    });

    return Object.values(
      map
    ).map((brand) => ({
      ...brand,

      orders:
        brand.orderIds.size,

      margin:
        !brand.incompleteCost &&
        brand.revenue > 0
          ? (
              brand.profit /
              brand.revenue
            ) * 100
          : null,
    }));
  }, [orders]);

  /*
  |--------------------------------------------------------------------------
  | Global statistics
  |--------------------------------------------------------------------------
  */

  const statistics =
    useMemo(() => {
      let revenue = 0;
      let cost = 0;
      let profit = 0;
      let units = 0;

      const orderIds =
        new Set();

      let incompleteCost =
        false;

      brandData.forEach(
        (brand) => {
          revenue +=
            brand.revenue;

          units +=
            brand.units;

          brand.orderIds.forEach(
            (id) =>
              orderIds.add(id)
          );

          if (
            brand.incompleteCost
          ) {
            incompleteCost =
              true;
          } else {
            cost += brand.cost;
            profit +=
              brand.profit;
          }
        }
      );

      const margin =
        !incompleteCost &&
        revenue > 0
          ? (profit / revenue) *
            100
          : null;

      return {
        revenue,

        cost:
          incompleteCost
            ? null
            : cost,

        profit:
          incompleteCost
            ? null
            : profit,

        margin,

        units,
        orders:
          orderIds.size,

        brands:
          brandData.length,

        incompleteCost,
      };
    }, [brandData]);

  /*
  |--------------------------------------------------------------------------
  | Search + sort
  |--------------------------------------------------------------------------
  */

  const visibleBrands =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      const result =
        brandData.filter(
          (brand) =>
            !query ||
            brand.brandName
              .toLowerCase()
              .includes(query)
        );

      result.sort((a, b) => {
        if (
          sortBy === "profit"
        ) {
          if (
            a.incompleteCost &&
            !b.incompleteCost
          ) {
            return 1;
          }

          if (
            !a.incompleteCost &&
            b.incompleteCost
          ) {
            return -1;
          }

          return (
            b.profit -
            a.profit
          );
        }

        if (
          sortBy === "margin"
        ) {
          return (
            (b.margin ?? -Infinity) -
            (a.margin ?? -Infinity)
          );
        }

        if (
          sortBy === "units"
        ) {
          return (
            b.units -
            a.units
          );
        }

        if (
          sortBy === "brand"
        ) {
          return a.brandName.localeCompare(
            b.brandName
          );
        }

        return (
          b.revenue -
          a.revenue
        );
      });

      return result;
    }, [
      brandData,
      search,
      sortBy,
    ]);

  /*
  |--------------------------------------------------------------------------
  | States
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="brand-admin-state">
        <div className="brand-admin-spinner" />

        <h2>
          Loading brand performance
        </h2>

        <p>
          Analyzing Stripe
          transactions...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="brand-admin-state brand-admin-error">
        <div className="brand-admin-state-icon">
          !
        </div>

        <h2>
          Unable to load brand
          performance
        </h2>

        <p>{error}</p>
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | UI
  |--------------------------------------------------------------------------
  */

  return (
    <div className="brand-admin-page">
      <div className="brand-admin-container">

        {/* HEADER */}

        <header className="brand-admin-header">
          <div>
            <div className="brand-admin-eyebrow">
              Malidag Admin
            </div>

            <h1>
              Brand performance
            </h1>

            <p>
              Revenue, supplier cost
              and gross profitability
              across your brands.
            </p>
          </div>

          <Link
            to="/userorder"
            className="brand-admin-payments-link"
          >
            View payments
          </Link>
        </header>

        {/* GLOBAL FINANCIALS */}

        <section className="brand-finance-grid">

          <StatCard
            label="Revenue"
            value={formatMoney(
              statistics.revenue
            )}
            description="Canonical USD sales"
          />

          <StatCard
            label="Product cost"
            value={
              statistics.cost !==
              null
                ? formatMoney(
                    statistics.cost
                  )
                : "—"
            }
            description="Brand / supplier cost"
          />

          <StatCard
            label="Gross profit"
            value={
              statistics.profit !==
              null
                ? formatMoney(
                    statistics.profit
                  )
                : "—"
            }
            description="Before fees and expenses"
            emphasis
          />

          <StatCard
            label="Gross margin"
            value={formatPercent(
              statistics.margin
            )}
            description="Product margin"
            emphasis
          />

        </section>

        {/* OPERATIONAL STATS */}

        <section className="brand-operational-grid">

          <div>
            <span>
              Brands
            </span>
            <strong>
              {statistics.brands}
            </strong>
          </div>

          <div>
            <span>
              Orders
            </span>
            <strong>
              {statistics.orders}
            </strong>
          </div>

          <div>
            <span>
              Units sold
            </span>
            <strong>
              {statistics.units}
            </strong>
          </div>

        </section>

        {/* DATA WARNING */}

        {statistics.incompleteCost && (
          <div className="brand-data-warning">
            <div className="brand-data-warning-icon">
              !
            </div>

            <div>
              <strong>
                Some historical
                transactions have no
                supplier cost data.
              </strong>

              <p>
                Revenue and unit totals
                are still available.
                Gross profit and margin
                remain hidden where cost
                information is
                incomplete.
              </p>
            </div>
          </div>
        )}

        {/* TOOLBAR */}

        <section className="brand-toolbar">
          <div className="brand-search-wrapper">
            <span className="brand-search-icon">
              ⌕
            </span>

            <input
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search brands..."
              className="brand-search-input"
            />
          </div>

          <select
            className="brand-sort-select"
            value={sortBy}
            onChange={(event) =>
              setSortBy(
                event.target.value
              )
            }
          >
            <option value="revenue">
              Sort by revenue
            </option>

            <option value="profit">
              Sort by gross profit
            </option>

            <option value="margin">
              Sort by margin
            </option>

            <option value="units">
              Sort by units
            </option>

            <option value="brand">
              Sort by brand
            </option>
          </select>
        </section>

        {/* BRAND CARDS */}

        {visibleBrands.length === 0 ? (
          <section className="brand-empty-state">
            <div className="brand-empty-icon">
              B
            </div>

            <h2>
              No brands found
            </h2>

            <p>
              No brand performance
              matches the current
              search.
            </p>
          </section>
        ) : (
          <section className="brand-card-grid">

            {visibleBrands.map(
              (brand) => (
                <article
                  key={
                    brand.brandName
                  }
                  className="brand-performance-card"
                >

                  {/* CARD HEADER */}

                  <div className="brand-card-header">
                    <div className="brand-identity">
                      <div className="brand-avatar">
                        {brand.brandName
                          .charAt(0)
                          .toUpperCase()}
                      </div>

                      <div>
                        <span>
                          Brand
                        </span>

                        <h2>
                          {
                            brand.brandName
                          }
                        </h2>
                      </div>
                    </div>

                    <div className="brand-margin-badge">
                      <span>
                        Margin
                      </span>

                      <strong>
                        {formatPercent(
                          brand.margin
                        )}
                      </strong>
                    </div>
                  </div>

                  {/* MAIN FINANCIALS */}

                  <div className="brand-card-financials">

                    <BrandMetric
                      label="Revenue"
                      value={formatMoney(
                        brand.revenue
                      )}
                    />

                    <BrandMetric
                      label="Product cost"
                      value={
                        !brand.incompleteCost
                          ? formatMoney(
                              brand.cost
                            )
                          : "—"
                      }
                    />

                    <BrandMetric
                      label="Gross profit"
                      value={
                        !brand.incompleteCost
                          ? formatMoney(
                              brand.profit
                            )
                          : "—"
                      }
                      highlight
                    />

                  </div>

                  {/* OPERATIONS */}

                  <div className="brand-card-meta">
                    <div>
                      <span>
                        Orders
                      </span>

                      <strong>
                        {brand.orders}
                      </strong>
                    </div>

                    <div>
                      <span>
                        Units
                      </span>

                      <strong>
                        {brand.units}
                      </strong>
                    </div>
                  </div>

                  {/* PERIODS */}

                  <div className="brand-period-section">
                    <div className="brand-period-heading">
                      Current performance
                    </div>

                    <div className="brand-period-grid">

                      <PeriodCard
                        label="Today"
                        period={
                          brand.today
                        }
                        formatMoney={
                          formatMoney
                        }
                      />

                      <PeriodCard
                        label="This week"
                        period={
                          brand.week
                        }
                        formatMoney={
                          formatMoney
                        }
                      />

                      <PeriodCard
                        label="This month"
                        period={
                          brand.month
                        }
                        formatMoney={
                          formatMoney
                        }
                      />

                      <PeriodCard
                        label="This year"
                        period={
                          brand.year
                        }
                        formatMoney={
                          formatMoney
                        }
                      />

                    </div>
                  </div>

                  {/* FOOTER */}

                  <div className="brand-card-footer">

                    {brand.incompleteCost ? (
                      <span className="brand-cost-warning">
                        Some cost data
                        unavailable
                      </span>
                    ) : (
                      <span className="brand-cost-complete">
                        Cost data complete
                      </span>
                    )}

                    <Link
                      to="/userorder"
                      className="brand-view-link"
                    >
                      View transactions
                      <span>→</span>
                    </Link>

                  </div>

                </article>
              )
            )}

          </section>
        )}

      </div>
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Components
|--------------------------------------------------------------------------
*/

function StatCard({
  label,
  value,
  description,
  emphasis = false,
}) {
  return (
    <div
      className={
        emphasis
          ? "brand-stat-card brand-stat-card-emphasis"
          : "brand-stat-card"
      }
    >
      <span className="brand-stat-label">
        {label}
      </span>

      <strong>
        {value}
      </strong>

      <small>
        {description}
      </small>
    </div>
  );
}

function BrandMetric({
  label,
  value,
  highlight = false,
}) {
  return (
    <div
      className={
        highlight
          ? "brand-metric brand-metric-highlight"
          : "brand-metric"
      }
    >
      <span>
        {label}
      </span>

      <strong>
        {value}
      </strong>
    </div>
  );
}

function PeriodCard({
  label,
  period,
  formatMoney,
}) {
  const profit =
    period.incompleteCost
      ? null
      : period.profit;

  const margin =
    !period.incompleteCost &&
    period.revenue > 0
      ? (
          period.profit /
          period.revenue
        ) * 100
      : null;

  return (
    <div className="brand-period-card">
      <span className="brand-period-label">
        {label}
      </span>

      <strong>
        {profit !== null
          ? formatMoney(profit)
          : "—"}
      </strong>

      <small>
        Gross profit
      </small>

      <div className="brand-period-details">
        <span>
          {formatMoney(
            period.revenue
          )}{" "}
          revenue
        </span>

        <span>
          {margin !== null
            ? `${margin.toFixed(
                1
              )}%`
            : "—"}{" "}
          margin
        </span>
      </div>
    </div>
  );
}

export default UserPaymentByBrand;